import type { PartialBlock } from '@blocknote/core';

import type { BlockRecord } from '@/lib/blocks/types';

export type StoredBlockContent = {
  props?: Record<string, unknown>;
  content?: PartialBlock['content'];
};

export type FlatBlock = {
  id: string;
  parentId: string;
  position: number;
  type: string;
  content: StoredBlockContent;
  syncVersion?: number;
};

export function blockContentToStored(content: Record<string, unknown>): StoredBlockContent {
  if ('props' in content || 'content' in content) {
    return {
      props: (content.props as Record<string, unknown> | undefined) ?? {},
      content: content.content as PartialBlock['content'],
    };
  }
  return { props: content, content: undefined };
}

export function storedToPartialBlock(row: BlockRecord): PartialBlock {
  const stored = blockContentToStored(row.content);
  return {
    id: row.id,
    type: row.type,
    props: stored.props ?? {},
    content: stored.content,
  } as PartialBlock;
}

export function blocksToPartialBlocks(pageId: string, rows: BlockRecord[]): PartialBlock[] {
  const byParent = new Map<string, BlockRecord[]>();
  for (const row of rows) {
    const key = row.parentId ?? pageId;
    const list = byParent.get(key) ?? [];
    list.push(row);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.position - b.position);
  }

  function build(parentId: string): PartialBlock[] {
    const children = byParent.get(parentId) ?? [];
    return children.map((row) => {
      const partial = storedToPartialBlock(row);
      return {
        ...partial,
        children: build(row.id),
      };
    });
  }

  return build(pageId);
}

export function partialBlockToStored(block: PartialBlock): StoredBlockContent {
  return {
    props: (block.props as Record<string, unknown> | undefined) ?? {},
    content: block.content,
  };
}

export function flattenPartialBlocks(
  parentId: string,
  blocks: PartialBlock[],
  syncVersions: Map<string, number>,
): FlatBlock[] {
  const result: FlatBlock[] = [];
  blocks.forEach((block, index) => {
    if (!block.id || !block.type) {
      return;
    }
    result.push({
      id: block.id,
      parentId,
      position: index,
      type: block.type,
      content: partialBlockToStored(block),
      syncVersion: syncVersions.get(block.id),
    });
    if (block.children?.length) {
      result.push(...flattenPartialBlocks(block.id, block.children, syncVersions));
    }
  });
  return result;
}

export function flatBlocksEqual(a: FlatBlock, b: FlatBlock): boolean {
  return (
    a.id === b.id &&
    a.parentId === b.parentId &&
    a.position === b.position &&
    a.type === b.type &&
    JSON.stringify(a.content) === JSON.stringify(b.content)
  );
}
